import {v4} from 'uuid';
import {isBool, isPlainObject, isString} from './index.js';
import {hasProperty} from './objects.js';
import {errorMessageFromApiResponse} from './notifications.js';

export enum FlyErrorSeverity {
  NONE = 'none',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum FlyErrorTypes {
  DEFAULT = 'default',
  REQUEST = 'request',
}

export interface FlyErrorData {
  severity: FlyErrorSeverity;
  label: string;
  timestamp: Date;
  description?: string;
  error: FlyError;
}

export type RegisterErrorDetail = {
  label: string;
  error: FlyError;
  description?: string;
  severity: FlyErrorSeverity;
};

export type RegisterErrorEvent = CustomEvent<RegisterErrorDetail>;

export type FlyErrorPart = {
  label: string;
  data: unknown;
  sourceErr?: Error;
  inline?: boolean;
};

/**
 * An error class for more helpful debugging.
 */
export class FlyError extends Error {
  cause = '';
  parts: FlyErrorPart[] = [];
  id = v4();
  severity: FlyErrorSeverity;

  constructor(
    severity: FlyErrorSeverity,
    message: string,
    ...parts: FlyErrorPart[]
  ) {
    super(message);
    this.name = 'FlyError';
    this.severity = severity;

    if (parts) {
      this.parts = parts.map(part => {
        if (part.data instanceof Error) {
          return {
            ...part,
            sourceErr: part.data,
            data: part.data.toString(),
          };
        }

        return part;
      });
    }

    Object.setPrototypeOf(this, FlyError.prototype);
  }

  register(label: string, description?: string): FlyError {
    const tag = 'debug:register-error';
    const event = new CustomEvent<RegisterErrorDetail>(tag, {
      detail: {
        label,
        description,
        error: this,
        severity: this.severity,
      },
    });

    dispatchEvent(event);

    return this;
  }
}

export class FlyErrorSafe extends FlyError {
  constructor(message: string, ...parts: FlyErrorPart[]) {
    super(FlyErrorSeverity.NONE, message, ...parts);
  }
}

export class FlyErrorConcern extends FlyError {
  constructor(message: string, ...parts: FlyErrorPart[]) {
    super(FlyErrorSeverity.LOW, message, ...parts);
  }
}

export class FlyErrorWarning extends FlyError {
  constructor(message: string, ...parts: FlyErrorPart[]) {
    super(FlyErrorSeverity.MEDIUM, message, ...parts);
  }
}

export class FlyErrorCritical extends FlyError {
  constructor(message: string, ...parts: FlyErrorPart[]) {
    super(FlyErrorSeverity.HIGH, message, ...parts);
  }
}

export function isAbortError(error: unknown): error is Error {
  return (
    (error instanceof Error && error.name === 'AbortError') ||
    (error instanceof FlyError &&
      hasProperty(error, 'aborted', isBool) &&
      error.aborted)
  );
}

export function isSafeError<T extends FlyErrorSafe>(
  error: unknown
): error is T {
  return error instanceof FlyError && error.severity === FlyErrorSeverity.NONE;
}

export class UnreachableError extends Error {
  name = 'UnreachableError';

  constructor(value: never) {
    super();
    this.message = `This error should be unreachable, but the value ${value} triggered it.`;
  }
}

type UntypedErrors =
  | string
  | string[]
  | Array<string | {[key: number]: string | string[]}>
  | Record<PropertyKey, unknown>
  | Array<Record<PropertyKey, unknown>>;

/**
 * A way of flattening errors when the error response is not type-safe.
 * @param errors - The error response to flatten
 * @returns a flattened Set of error message strings
 */
export function flattenUntypedErrorsToStringSet(
  errors: UntypedErrors
): Set<string> {
  const flattenedErrors = new Set<string>();

  function flatten(error: UntypedErrors | {[key: number]: string | string[]}) {
    if (isString(error)) return flattenedErrors.add(error);
    if (Array.isArray(error)) return error.forEach(flatten);
    if (isPlainObject(error)) {
      return flattenedErrors.add(errorMessageFromApiResponse(error));
    }
  }

  try {
    flatten(errors);
  } catch (err) {
    console.error('Could not flatten errors', err);
  }
  return flattenedErrors;
}
